export const zoneWallClockFields = (instant: Date, zone: string) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(instant);
  const get = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value ?? "0");
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    // Intl can render midnight as "24" — normalize to 0.
    hour: get("hour") % 24,
    minute: get("minute"),
    second: get("second"),
  };
};

/** Venue wall-clock fields in UTC slots, for calendar arithmetic without browser DST. */
export const instantToWallClockDate = (iso: string, zone: string): Date => {
  const f = zoneWallClockFields(new Date(iso), zone);
  return new Date(
    Date.UTC(f.year, f.month - 1, f.day, f.hour, f.minute, f.second),
  );
};

/** Convert a UTC wall-clock surrogate back to a true instant in the venue zone. */
export const wallClockDateToInstantIso = (date: Date, zone: string): string => {
  const asUtc = date.getTime();
  const f = zoneWallClockFields(date, zone);
  const zoneRendered = Date.UTC(
    f.year,
    f.month - 1,
    f.day,
    f.hour,
    f.minute,
    f.second,
  );
  const candidate = new Date(asUtc - (zoneRendered - asUtc));
  // The venue offset can change between the surrogate and the candidate.
  const resolved = zoneWallClockFields(candidate, zone);
  const resolvedRendered = Date.UTC(
    resolved.year,
    resolved.month - 1,
    resolved.day,
    resolved.hour,
    resolved.minute,
    resolved.second,
  );
  const corrected = new Date(asUtc - (resolvedRendered - candidate.getTime()));
  const fields = zoneWallClockFields(corrected, zone);
  const matches =
    fields.year === date.getUTCFullYear() &&
    fields.month === date.getUTCMonth() + 1 &&
    fields.day === date.getUTCDate() &&
    fields.hour === date.getUTCHours() &&
    fields.minute === date.getUTCMinutes() &&
    fields.second === date.getUTCSeconds();
  // A nonexistent venue time cannot round-trip: keep the initial gap resolution.
  return (matches ? corrected : candidate).toISOString();
};

